import { ContextMenu, ContextMenuItem, ContextMenuModel } from "./custom-context-menu";
import { TimesheetCommentsManager } from "./timesheet-comments-manager";
import { TimesheetRightTableDataManager } from "./timesheet-right-table-data-manager";
import { Txt } from "../common/text";
import { C } from "../common/component";
import { Dialog } from "../common/dialog";
import { TimesheetSelectedCells } from "./timesheet-selected-cells";
import { TimesheetCellButton } from "./timesheet-models";
import { TimesheetCopyPasteManager } from "./timesheet-copy-paste-manager";
import { TimesheetRightTableFormatter } from "./timesheet-right-table-formatter";

export class TimesheetContextMenuBuilder
{
    private handler?: (e: MouseEvent) => void;
    private menu?: ContextMenu;
    private readonly targets: HTMLElement[] = [];

    constructor(private readonly commentsManager: TimesheetCommentsManager,
                private readonly dataManager: TimesheetRightTableDataManager,
                private readonly formatter: TimesheetRightTableFormatter,
                private readonly copyPasteManager: TimesheetCopyPasteManager,
                private readonly multiCellButtons: TimesheetCellButton[])
    {
    }

    attach(hot: Handsontable): void
    {
        const anyHot = hot as any;
        const container: HTMLElement = anyHot.container ?? anyHot.rootElement;

        const selectors = [
            container,
            ".ht_master",
            ".ht_clone_top",
            ".ht_clone_left",
            ".ht_clone_corner"
        ];

        selectors.forEach((selector) =>
        {
            const element = typeof selector === "string"
                ? container.querySelector<HTMLElement>(selector)
                : selector;

            if (element)
            {
                if (!this.handler)
                {
                    this.handler = (e) => this.onContextMenu(e, hot);
                }
                element.addEventListener("contextmenu", this.handler, true);
                this.targets.push(element);
            }
        });
    }

    destroy(): void
    {
        if (this.handler)
        {
            this.targets.forEach((target) => target.removeEventListener("contextmenu", this.handler, true));
        }
        this.menu?.destroy();
        this.targets.length = 0;
    }

    private onContextMenu(e: MouseEvent, hot: Handsontable): void
    {
        e.preventDefault();
        e.stopPropagation();

        const td = (e.target as HTMLElement).closest("td");
        if (!td)
        {
            return;
        }
        const coords = this.getCellCoords(td, hot);
        if (!coords)
        {
            return;
        }

        const selectedCells = TimesheetSelectedCells.get(hot);
        if (selectedCells == null || coords.row < selectedCells.startRow || coords.row > selectedCells.endRow ||
            coords.col < selectedCells.startColumn || coords.col > selectedCells.endColumn )
        {
            return;
        }

        this.menu?.destroy();
        this.menu = new ContextMenu(this.buildModel(selectedCells, hot));
        this.menu.open(e);
    }

    private getCellCoords(td: HTMLElement, hot: Handsontable): any
    {
        const coords = hot.getCoords(td) as any;
        if (!coords || coords.row < 0 || coords.col < 0)
        {
            return null;
        }
        return coords;
    }

    private buildModel(selectedCells: TimesheetSelectedCells, hot: Handsontable): ContextMenuModel
    {
        return {
            items: [
                {
                    text: () => Txt.copy,
                    onClick: async ()=> await this.copyPasteManager.copyFromButton(selectedCells)
                },
                {
                    text: () => Txt.paste,
                    onClick: async ()=> await this.copyPasteManager.pasteFromButton(selectedCells)
                },
                ...this.createEditCommentItem(selectedCells, hot),
                ...this.createChangeRowCommentItem(selectedCells, hot),
                ...this.createCustomButtons(selectedCells)
            ]
        };
    }

    private createCustomButtons(selectedCells: TimesheetSelectedCells): ContextMenuItem[]
    {
        return this.createCustomButtonsCore(selectedCells.isSingleCell()
            ? this.dataManager.getButtons(selectedCells.startRow, selectedCells.startColumn)
            : this.multiCellButtons, selectedCells);
    }

    private createCustomButtonsCore(buttons: TimesheetCellButton[], selectedCells: TimesheetSelectedCells): ContextMenuItem[]
    {
        const result: ContextMenuItem[] = [];
        for (const button of buttons)
        {
            result.push({
                text: () => button.Name,
                disabled: () => button.IsDisabled,
                onClick: button.Url ? () => this.dataManager.doButtonAction(button, selectedCells) : null,
                submenu: button.SubButtons.length > 0 ? this.createCustomButtonsCore(button.SubButtons, selectedCells) : null
            });
        }
        return result;
    }

    private createEditCommentItem(selectedCells: TimesheetSelectedCells, hot: Handsontable): ContextMenuItem[]
    {
        if (!selectedCells.isSingleCell())
        {
            return [];
        }
        return [{
            text: () => Txt.addComment,
            disabled: () => !this.commentsManager.isCommentEditable(selectedCells.startRow),
            onClick: () =>
            {
                const cell = hot.getCell(selectedCells.startRow, selectedCells.startColumn);
                const cellElement = C.create(<HTMLElement>cell);
                this.commentsManager.openNewComment(cellElement, selectedCells.startRow, selectedCells.startColumn);
            }
        }];
    }

    private createChangeRowCommentItem(selectedCells: TimesheetSelectedCells, hot: Handsontable): ContextMenuItem[]
    {
        if (selectedCells.startRow !== selectedCells.endRow)
        {
            return [];
        }
        return [{
            text: () => Txt.changeRowComment,
            disabled: () => !this.commentsManager.isCommentEditable(selectedCells.startRow),
            onClick: () =>
            {
                const row = selectedCells.startRow;

                const modal = new Dialog(Txt.comment);
                modal.body.classList.add("change-password");

                const currentComment = this.commentsManager.getComment(row, null);

                const commentInput = C.input
                    .addClass("form-control")
                    .attr("type", "text")
                    .attr("placeholder", Txt.comment)
                    .val(currentComment)
                    .appendTo(modal.body);

                const saveButton = C.button
                    .addClass("btn btn-primary main-button")
                    .text(Txt.save)
                    .appendTo(modal.body);

                saveButton.click(async () =>
                {
                    const commentValue = String(commentInput.val());
                    await this.commentsManager.saveComment(row, null, commentValue);
                    const rowHeaderFn = (rowNumber: number): string =>
                        this.formatter.getRowHeader(rowNumber, this.commentsManager);
                    hot.updateSettings({ rowHeaders: rowHeaderFn as unknown as any }, false);
                    modal.hide();
                });

                modal.show();
            }
        }];
    }

}
